import { Location } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { DateRange } from '@angular/material/datepicker';
import { FeatureFlag } from '@app/enums';
import { LocalizedDatePipe } from '@app/pipes/localized-date/localized-date.pipe';
import { AuthService } from '@app/services';
import { FeatureService } from '@app/services/feature.service';
import { Employee } from '@models/employee/employee.model';
import { CompanySetting } from '@models/settings/company-setting.model';
import { TimeOffPolicy } from '@models/time-off-v3/time-off-policy.model';
import { TimeOffRequestDay } from '@models/time-off-v3/time-off-request-day.model';
import { TimeOffRequest, TimeOffRequestStatus } from '@models/time-off-v3/time-off-request.model';
import { RequestDayForm, RequestDaysForm } from '@time-off-v3/forms';
import { TimeOffBalance } from '@time-off-v3/interfaces/time-off-balance.interface';
import { TimeOffDateFormatDashes } from '@time-off-v3/meta/time-off-meta';
import { TimeOffService } from '@time-off-v3/services';
import { subDays } from 'date-fns';
import debounce from 'lodash-es/debounce';
import flatten from 'lodash-es/flatten';
import uniqBy from 'lodash-es/uniqBy';
import moment, { Moment } from 'moment';

export type ActionType = 'update' | 'create';

const visibilitySetting = 'timeOffRequests.visibility';
const DEBOUNCE_TIME = 500;
const HOURS_PER_DAY = 24;
const ONE_DAY = 1;

@Component({
    selector: 'app-time-off-request',
    templateUrl: './time-off-request.template.html',
    styleUrls: ['./time-off-request.style.scss', '../../../../styles/shared-views/time-off-request.scss'],
})
export class TimeOffRequestComponent implements OnInit {
    @Input() timeOffRequest: TimeOffRequest;
    @Input() actionType: ActionType = 'create';
    @Output() save: EventEmitter<TimeOffRequest> = new EventEmitter<TimeOffRequest>();
    @Output() loading: EventEmitter<boolean> = new EventEmitter<boolean>();
    @ViewChild('requestDayForm') requestDayForm: RequestDayForm;
    @ViewChild('requestDaysForm') requestDaysForm: RequestDaysForm;
    activeTimeOffPolicies: TimeOffPolicy[] = [];
    overlaps: TimeOffRequest[] = [];
    isLoading = false;
    isUpdatingBalance = false;
    isVerifyingBusinessRules = false;
    isUpdatingOverlaps = false;
    isSyncingTimeOffRequestDays = false;
    timeOffBalance: TimeOffBalance;
    visibleTimeOff: boolean;
    canViewOverlaps: boolean;
    employee: Employee;
    warnings: string[] = [];
    errors: string[] = [];
    beginDateSelected = false;
    endDateToBeSelected = false;
    isRequestDaysDialogOpen = false;

    canUpdateDatePicker = true;

    private isAdminOrManagerEditing = false;
    private readonly verifyBusinessRules = debounce(async (): Promise<void> => {
        this.isVerifyingBusinessRules = true;

        this.warnings = [];
        this.errors = [];

        const { warnings, errors } = await this.timeOffService.verifyBusinessRules(this.timeOffRequest);

        this.warnings = warnings ?? [];
        this.errors = errors ?? [];

        this.isVerifyingBusinessRules = false;

        // If there is already an error for Negative balance cap we don't need to proceed and check for negative warning
        if (this.containsNegativeBalanceCapError()) {
            return;
        }

        if (this.timeOffBalance.isUnlimited) {
            return;
        }

        // If there is no error for negative balance we check to see if we need to show warning
        const balanceBefore = this.showHourly ? this.timeOffBalance.balanceHours : this.timeOffBalance.balance;
        const newBalance = balanceBefore - this.totalAmount;

        if (newBalance >= 0) {
            return;
        }

        this.warnings.push('time-off-v3.thisRequestMayResultInANegativeBalance');
    }, DEBOUNCE_TIME);

    constructor(
        private localizedDatePipe: LocalizedDatePipe,
        private timeOffService: TimeOffService,
        private auth: AuthService,
        private location: Location,
        private featureService: FeatureService
    ) {}

    dateFilter = (date: Moment): boolean => {
        // Dates can be restricted on update only
        if (this.actionType === 'create') {
            return true;
        }

        // Admin and managers has no restriction on dates
        if (this.isAdminOrManagerEditing) {
            return true;
        }

        // There is no date restriction on pending requests
        if (this.timeOffRequest?.status === TimeOffRequestStatus.pending) {
            return true;
        }

        return date.isSameOrAfter(moment(), 'day');
    };

    get startAtString(): string {
        return this.localizedDatePipe.transform(this.timeOffRequest.startAt, 'short');
    }

    get endAtString(): string {
        return this.localizedDatePipe.transform(this.timeOffRequest.endAt, 'short');
    }

    get selectedTimeOffPolicy(): TimeOffPolicy {
        return this.activeTimeOffPolicies.find((policy) => policy.id === this.timeOffRequest.timeOffPolicyId);
    }

    get showHourly(): boolean {
        return this.timeOffRequest.timeOffPolicy.timeOffType.displayInHours;
    }

    get totalAmount(): number {
        return this.timeOffRequest.timeOffRequestDays.map((day) => +day.amount).reduce((acc, amount) => acc + amount);
    }

    get timeOffRequestDays(): TimeOffRequestDay[] {
        return this.timeOffRequest.timeOffRequestDays as TimeOffRequestDay[];
    }

    get hasBanner(): boolean {
        return !!this.selectedTimeOffPolicy.timeOffType.description || !!this.errors?.length || !!this.warnings?.length;
    }

    get weekendsCount(): number {
        return this.timeOffRequest.timeOffRequestDays.filter((requestDay) => requestDay.isWeekend).length;
    }

    get weekendsCountMessage(): string {
        const weekendsCount = this.weekendsCount;

        if (weekendsCount === 1) {
            return 'time-off-v3.weekendDay';
        }

        return 'time-off-v3.weekendDays';
    }

    get holidaysCount(): number {
        return this.timeOffRequest.timeOffRequestDays.filter((requestDay) => requestDay.isHoliday).length;
    }

    get holidaysCountMessage(): string {
        const holidaysCount = this.holidaysCount;

        if (holidaysCount === 1) {
            return 'time-off-v3.holiday';
        }

        return 'time-off-v3.holidaysLowercase';
    }

    get holidaysMessage(): string {
        return this.weekendsCount && this.holidaysCount ? this.holidaysCountMessage : '';
    }

    get daysMessage(): string {
        return this.weekendsCount ? this.weekendsCountMessage : this.holidaysCountMessage;
    }

    get maxAmount(): number {
        return this.selectedTimeOffPolicy?.timeOffType?.displayInHours ? HOURS_PER_DAY : ONE_DAY;
    }

    get hasErrors(): boolean {
        return !this.isAdminOrManagerEditing && !!this.errors.length;
    }

    async ngOnInit(): Promise<void> {
        this.isAdminOrManagerEditing =
            this.auth.isAdmin() || this.timeOffRequest.employee?.reportsToEmployee(this.auth.employee);

        this.toggleLoading();

        this.setEmployee();

        await this.setPermissions();

        this.setOverlaps();

        await this.setActivePolicies();

        this.setDefaultPolicy();

        await this.setTimeOffBalance();

        await this.setInitialTimeOffRequestDays();

        await this.verifyBusinessRules();

        this.toggleLoading();
    }

    async processRequest(dateRange: DateRange<Moment>): Promise<void> {
        this.canUpdateDatePicker = false;
        this.timeOffRequest.startAt = dateRange.start.toDate();
        this.timeOffRequest.endAt = dateRange.end.toDate();
        this.setTimeOffRequestDays();
        await this.setOverlaps();
        await this.setTimeOffBalance();
        await this.verifyBusinessRules();
        this.beginDateSelected = false;
        this.canUpdateDatePicker = true;

        const isSameDay = dateRange.start.isSame(dateRange.end, 'day');
        if (!isSameDay) {
            this.endDateToBeSelected = false;
        }
    }

    processBeginDateSelected(date: Moment): void {
        this.timeOffRequest.startAt = date.toDate();
        this.beginDateSelected = true;
        const dateRange: DateRange<Moment> = new DateRange(date, date);
        this.processRequest(dateRange);
        this.endDateToBeSelected = true;
    }

    onAdjustDailyAmounts(): void {
        this.requestDaysForm.beforeShow();
        this.isRequestDaysDialogOpen = true;
    }

    closeDialog(): void {
        this.isRequestDaysDialogOpen = false;
    }

    onSaveRequestDays(requestDaysForm: RequestDaysForm): void {
        this.requestDaysForm.submit();

        if (!this.requestDaysForm.valid) {
            return;
        }

        this.timeOffRequest.timeOffRequestDays = flatten(requestDaysForm.requestDaysGrouped);

        this.verifyBusinessRules();

        this.closeDialog();
    }

    async setPolicy(policyId: number): Promise<void> {
        this.timeOffRequest.timeOffPolicyId = policyId;
        this.timeOffRequest.timeOffPolicy = this.activeTimeOffPolicies.find((policy) => policy.id === policyId);
        await this.setTimeOffRequestDays();
        await this.setTimeOffBalance();
        this.verifyBusinessRules();
    }

    goBack(): void {
        this.location.back();
    }

    setTimeOffRequestDayAmount(timeOffRequestDay: TimeOffRequestDay): void {
        this.timeOffRequestDays[0] = timeOffRequestDay;
        this.verifyBusinessRules();
    }

    onSave(): void {
        /*
         * Regarding requestDayForm.
         * The input data is sent to our API for verification. We update the angular model on blur to prevent
         * constant requests for verification.
         *
         * This blur here is to fix a scenario where the user doesn't click off the input before clicking the submit button.
         */
        this.requestDayForm?.timeOffRequestDayInput.nativeElement.blur();
        this.requestDayForm?.submit();

        if (!this.timeOffRequest.hasMultipleDays && !this.requestDayForm?.valid) {
            return;
        }

        this.save.emit(this.timeOffRequest);
    }

    private async setTimeOffRequestDays(): Promise<void> {
        this.isSyncingTimeOffRequestDays = true;

        await this.timeOffRequest.syncTimeOffRequestDays();

        this.isSyncingTimeOffRequestDays = false;
    }

    private async setInitialTimeOffRequestDays(): Promise<void> {
        // If it's a create view we use preview end point to populate request days
        if (this.actionType === 'create') {
            await this.setTimeOffRequestDays();
        } else {
            // otherwise we use the stored data in time off request days
            this.timeOffRequest.timeOffRequestDays.forEach((requestDay) => {
                requestDay.timeOffType = this.timeOffRequest.timeOffPolicy.timeOffType;
            });
        }
    }

    private async setOverlaps(): Promise<void> {
        if (!this.canViewOverlaps) {
            return;
        }

        this.isUpdatingOverlaps = true;

        const [timeOffRequests]: [TimeOffRequest[], any] = await TimeOffRequest.with('employee')
            .where('startAt', {
                to: moment(this.timeOffRequest.endAt, TimeOffDateFormatDashes).format(TimeOffDateFormatDashes),
            })
            .where('endAt', {
                from: moment(this.timeOffRequest.startAt, TimeOffDateFormatDashes).format(TimeOffDateFormatDashes),
            })
            .where('departmentId', this.employee.departmentId)
            .whereIn('status', ['approved', 'pending'])
            .when(this.actionType === 'update', (query) =>
                query.where('excludeTimeOffRequests', this.timeOffRequest.id)
            )
            .get();

        this.overlaps = uniqBy<TimeOffRequest>(
            timeOffRequests.filter((timeOffRequest) => timeOffRequest.employeeId !== this.timeOffRequest.employeeId),
            'employeeId'
        );

        this.isUpdatingOverlaps = false;
    }

    private toggleLoading(): void {
        this.isLoading = !this.isLoading;
        this.loading.emit(this.isLoading);
    }

    private setEmployee(): void {
        this.employee = this.timeOffRequest.employee ?? this.auth.employee;
    }

    private getFirstAvailablePolicy(): TimeOffPolicy {
        return this.timeOffRequest.timeOffPolicy ?? this.activeTimeOffPolicies[0];
    }

    private async getTimeOffBalance(): Promise<TimeOffBalance> {
        return await this.timeOffService.getBalance(this.employee.id, {
            'filters[processedAt][to]': this.localizedDatePipe.transform(
                subDays(this.timeOffRequest.startAt, 1),
                'longYearDate'
            ),
            'filters[timeOffTypeId]': this.selectedTimeOffPolicy?.timeOffTypeId,
        });
    }

    private async setPermissions(): Promise<void> {
        const [companySettings] = await CompanySetting.param('company', this.auth.company.id)
            .where('module', 'TimeOffv3')
            .with('setting')
            .get();

        this.visibleTimeOff = companySettings.find((setting) => setting.setting.name === visibilitySetting).value;

        this.canViewOverlaps =
            this.visibleTimeOff || this.auth.can('administrate') || this.auth.employee.id === this.employee.reportsTo;
    }

    private setDefaultPolicy(): void {
        if (this.timeOffRequest.timeOffPolicy) {
            return;
        }

        this.timeOffRequest.timeOffPolicy = this.getFirstAvailablePolicy();
        this.timeOffRequest.timeOffPolicyId = this.getFirstAvailablePolicy().id;
    }

    private async setActivePolicies(): Promise<void> {
        if (this.actionType === 'update') {
            this.activeTimeOffPolicies = [this.timeOffRequest.timeOffPolicy];
            return;
        }

        this.activeTimeOffPolicies = (
            await Employee.with(['activeTimeOffPolicies.timeOffType', 'activeTimeOffPolicies.timeOffRequestRule'])
                .param('company', this.auth.company.id)
                .find(this.employee.id)
        ).activeTimeOffPolicies;
    }

    private async setTimeOffBalance(): Promise<void> {
        this.isUpdatingBalance = true;

        this.timeOffBalance = await this.getTimeOffBalance();

        this.isUpdatingBalance = false;
    }

    private containsNegativeBalanceCapError(): boolean {
        return !!this.errors.length && this.errors.some((error) => error.includes('Negative'));
    }
}
